import { apiClient } from './client';
import type {
  DocumentTemplate,
  DocumentTemplateSummary,
  DocumentTemplateCreate,
  DocumentTemplateUpdate,
  CustomDocumentConfig,
  CustomPlaceholderCreate,
  CustomPlaceholderUpdate,
  DocumentTriggerRule,
  DocumentTriggerRuleCreate,
  DocumentTriggerRuleUpdate,
  DocumentGenerationRequest,
  DocumentGenerationResult,
  DocumentAuditRecord,
  BatchGenerateRequest,
  BatchJobStatus,
} from '../../types/customDocuments';
import type { ConditionGroup } from '../../types/rulesEngine';

class CustomDocumentService {

  // ---- Templates ----

  async listTemplates(clientId: string, context?: string): Promise<DocumentTemplateSummary[]> {
    const params = context ? `?context=${encodeURIComponent(context)}` : '';
    const resp = await apiClient.get<DocumentTemplateSummary[]>(
      `/api/v1/custom-documents/${clientId}/templates${params}`
    );
    return resp.data;
  }

  async getTemplate(clientId: string, templateId: string): Promise<DocumentTemplate> {
    const resp = await apiClient.get<DocumentTemplate>(
      `/api/v1/custom-documents/${clientId}/templates/${templateId}`
    );
    return resp.data;
  }

  async createTemplate(
    clientId: string,
    data: DocumentTemplateCreate
  ): Promise<DocumentTemplate> {
    const resp = await apiClient.post<DocumentTemplate>(
      `/api/v1/custom-documents/${clientId}/templates`,
      data
    );
    return resp.data;
  }

  async updateTemplate(
    clientId: string,
    templateId: string,
    data: DocumentTemplateUpdate
  ): Promise<DocumentTemplate> {
    const resp = await apiClient.patch<DocumentTemplate>(
      `/api/v1/custom-documents/${clientId}/templates/${templateId}`,
      data
    );
    return resp.data;
  }

  async deleteTemplate(clientId: string, templateId: string): Promise<void> {
    await apiClient.delete(`/api/v1/custom-documents/${clientId}/templates/${templateId}`);
  }

  async duplicateTemplate(
    clientId: string, templateId: string
  ): Promise<DocumentTemplate> {
    const resp = await apiClient.post<DocumentTemplate>(
      `/api/v1/custom-documents/${clientId}/templates/${templateId}/duplicate`
    );
    return resp.data;
  }

  async setTemplateActive(
    clientId: string, templateId: string, isActive: boolean
  ): Promise<DocumentTemplate> {
    const resp = await apiClient.patch<DocumentTemplate>(
      `/api/v1/custom-documents/${clientId}/templates/${templateId}/active`,
      { isActive }
    );
    return resp.data;
  }

  // ---- Custom Placeholders ----

  async getConfig(clientId: string): Promise<CustomDocumentConfig> {
    const resp = await apiClient.get<CustomDocumentConfig>(
      `/api/v1/custom-documents/${clientId}/config`
    );
    return resp.data;
  }

  async createPlaceholder(
    clientId: string,
    data: CustomPlaceholderCreate
  ): Promise<CustomDocumentConfig> {
    const resp = await apiClient.post<CustomDocumentConfig>(
      `/api/v1/custom-documents/${clientId}/config/placeholders`,
      data
    );
    return resp.data;
  }

  async updatePlaceholder(
    clientId: string,
    placeholderId: string,
    data: CustomPlaceholderUpdate
  ): Promise<CustomDocumentConfig> {
    const resp = await apiClient.patch<CustomDocumentConfig>(
      `/api/v1/custom-documents/${clientId}/config/placeholders/${placeholderId}`,
      data
    );
    return resp.data;
  }

  async deletePlaceholder(clientId: string, placeholderId: string): Promise<void> {
    await apiClient.delete(
      `/api/v1/custom-documents/${clientId}/config/placeholders/${placeholderId}`
    );
  }

  async getPlaceholderImageUrl(
    clientId: string,
    placeholderId: string
  ): Promise<{ signedUrl: string; expiresInMinutes: number }> {
    const resp = await apiClient.get<{ signedUrl: string; expiresInMinutes: number }>(
      `/api/v1/custom-documents/${clientId}/config/placeholders/${placeholderId}/image`
    );
    return resp.data;
  }

  async uploadPlaceholderImage(
    clientId: string,
    placeholderId: string,
    file: File
  ): Promise<{ storagePath: string; signedUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const resp = await apiClient.postFormData<{ storagePath: string; signedUrl: string }>(
      `/api/v1/custom-documents/${clientId}/config/placeholders/${placeholderId}/image`,
      formData
    );
    return resp.data;
  }

  // ---- Trigger Rules ----

  async listRules(clientId: string): Promise<DocumentTriggerRule[]> {
    const resp = await apiClient.get<DocumentTriggerRule[]>(
      `/api/v1/custom-documents/${clientId}/rules`
    );
    return resp.data;
  }

  async createRule(
    clientId: string,
    data: DocumentTriggerRuleCreate
  ): Promise<DocumentTriggerRule> {
    const resp = await apiClient.post<DocumentTriggerRule>(
      `/api/v1/custom-documents/${clientId}/rules`,
      data
    );
    return resp.data;
  }

  async updateRule(
    clientId: string,
    ruleId: string,
    data: DocumentTriggerRuleUpdate
  ): Promise<DocumentTriggerRule> {
    const resp = await apiClient.patch<DocumentTriggerRule>(
      `/api/v1/custom-documents/${clientId}/rules/${ruleId}`,
      data
    );
    return resp.data;
  }

  async deleteRule(clientId: string, ruleId: string): Promise<void> {
    await apiClient.delete(`/api/v1/custom-documents/${clientId}/rules/${ruleId}`);
  }

  async setRuleActive(
    clientId: string, ruleId: string, isActive: boolean
  ): Promise<DocumentTriggerRule> {
    const resp = await apiClient.patch<DocumentTriggerRule>(
      `/api/v1/custom-documents/${clientId}/rules/${ruleId}/active`,
      { isActive }
    );
    return resp.data;
  }

  // ---- Generation + Records ----

  async generateDocuments(
    clientId: string,
    request: DocumentGenerationRequest
  ): Promise<DocumentGenerationResult[]> {
    const resp = await apiClient.post<{ results: DocumentGenerationResult[] }>(
      `/api/v1/custom-documents/${clientId}/generate`,
      request
    );
    return resp.data.results;
  }

  async listAuditRecords(clientId: string): Promise<DocumentAuditRecord[]> {
    const resp = await apiClient.get<{ records: DocumentAuditRecord[] }>(
      `/api/v1/custom-documents/${clientId}/records`
    );
    return resp.data.records;
  }

  async deleteAuditRecord(clientId: string, recordId: string): Promise<void> {
    await apiClient.delete(`/api/v1/custom-documents/${clientId}/records/${recordId}`);
  }

  async getDownloadUrl(
    clientId: string,
    recordId: string
  ): Promise<{ signedUrl: string; expiresInMinutes: number; filename: string }> {
    const resp = await apiClient.get<{
      signedUrl: string; expiresInMinutes: number; filename: string
    }>(`/api/v1/custom-documents/${clientId}/records/${recordId}/download`);
    return resp.data;
  }

  // ---- Batch Generation ----

  async countMatchingTrades(
    clientId: string,
    conditionGroups: ConditionGroup[]
  ): Promise<{ count: number }> {
    const resp = await apiClient.post<{ count: number }>(
      `/api/v1/custom-documents/${clientId}/batch/count`,
      { conditionGroups }
    );
    return resp.data;
  }

  async createBatchJob(
    clientId: string,
    request: BatchGenerateRequest
  ): Promise<{ jobId: string; totalItems: number }> {
    const resp = await apiClient.post<{ jobId: string; totalItems: number }>(
      `/api/v1/custom-documents/${clientId}/batch`,
      request
    );
    return resp.data;
  }

  async listBatchJobs(clientId: string): Promise<BatchJobStatus[]> {
    const resp = await apiClient.get<{ jobs: BatchJobStatus[] }>(
      `/api/v1/custom-documents/${clientId}/batch`
    );
    return resp.data.jobs;
  }

  async getBatchJob(clientId: string, jobId: string): Promise<BatchJobStatus> {
    const resp = await apiClient.get<BatchJobStatus>(
      `/api/v1/custom-documents/${clientId}/batch/${jobId}`
    );
    return resp.data;
  }

  async dismissBatchJob(clientId: string, jobId: string): Promise<void> {
    await apiClient.post(`/api/v1/custom-documents/${clientId}/batch/${jobId}/dismiss`);
  }
}

export const customDocumentService = new CustomDocumentService();
export default customDocumentService;
